// Store
import useSiteStore from '../../store/siteStore.js';
import useCoachStore from '../../store/coachStore.js';

// Components
import { ExpandMoreOutlined, AddOutlined, RemoveOutlined } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, IconButton, Typography } from '@mui/material';
import CustomTextField from './TextField.js';

// Style
import { grey, orange, blue } from '@mui/material/colors/index.js';
import { darkTheme } from '../../styles/colorSchemes/index.js';

// Hooks
import { useAppTheme } from '../../hooks/useAppTheme.js';


const CreateWeeklyScheduleSettings = (props) => {
    const { repetitions, onChangeStartWeek, startWeek, styles } = props;

    const { setRepetitions } = useCoachStore();
    const { year, setYear } = useSiteStore();
    const { isDarkMode } = useAppTheme();


    return (
        <Accordion
            sx={{
                background: isDarkMode ? darkTheme.accordionBg : grey[100],
                "&.MuiAccordion-root.Mui-expanded": { mb: "2px", mt: "0px" },
            }}
            defaultExpanded={true}
        >
            <AccordionSummary 
                expandIcon={<ExpandMoreOutlined sx={{ color: isDarkMode ? darkTheme.textSecondary : undefined }} />}
            >
                <Typography sx={{ color: isDarkMode ? darkTheme.textPrimary : undefined }}>Einstellungen</Typography>
            </AccordionSummary>

            <AccordionDetails className={styles.accordionsettings} sx={{ bgcolor: isDarkMode ? darkTheme.headerBg : undefined }}>

                {/* Wiederholungen */}
                <Box 
                    className={styles.repetitions}
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: isDarkMode ? darkTheme.cardBg : 'white',
                        border: `1px solid ${isDarkMode ? darkTheme.cardBorder : grey[200]}`,
                        boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.05)',
                    }}
                >
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            color: isDarkMode ? darkTheme.textSecondary : grey[600],
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            mb: 0.5,
                            display: 'block'
                        }}
                    >
                        Wiederholungen
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                            size="small"
                            disabled={repetitions <= 1}
                            onClick={() => { if (repetitions > 1) setRepetitions(repetitions - 1); }}
                            sx={{
                                backgroundColor: isDarkMode ? darkTheme.cardBg : grey[100],
                                width: 32,
                                height: 32,
                                color: isDarkMode ? darkTheme.textPrimary : grey[700],
                                border: `1px solid ${isDarkMode ? darkTheme.cardBorder : grey[300]}`,
                                '&:hover': {
                                    backgroundColor: isDarkMode ? darkTheme.cardHover : grey[200],
                                    borderColor: isDarkMode ? orange[500] : blue[500],
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : grey[50],
                                    color: isDarkMode ? darkTheme.textSecondary : grey[400],
                                    borderColor: isDarkMode ? darkTheme.cardBorder : grey[200],
                                }
                            }}
                        >
                            <RemoveOutlined sx={{ fontSize: '1rem' }} />
                        </IconButton>
                        <CustomTextField
                            className={styles.textfield}
                            type="number"
                            variant="outlined"
                            size="small"
                            value={repetitions}
                            onChange={(e) => {
                                const val = Number(e.target.value);
                                if (val >= 1 && val <= 52) setRepetitions(val);
                            }}
                            sx={{
                                width: 70,
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: isDarkMode ? darkTheme.cardBg : 'white',
                                    borderRadius: 1,
                                    '& input': {
                                        textAlign: 'center',
                                        fontWeight: 600,
                                        fontSize: '1rem',
                                        padding: '6px 8px',
                                        MozAppearance: 'textfield',
                                        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                            WebkitAppearance: 'none',
                                            margin: 0,
                                        },
                                    },
                                    '& fieldset': { borderColor: isDarkMode ? darkTheme.cardBorder : grey[300] },
                                    '&:hover fieldset': { borderColor: isDarkMode ? orange[500] : blue[500] },
                                    '&.Mui-focused fieldset': { borderColor: isDarkMode ? orange[500] : blue[600] },
                                },
                                '& .MuiInputBase-input': { color: isDarkMode ? darkTheme.textPrimary : grey[800] },
                            }}
                        />
                        <IconButton
                            size="small"
                            disabled={repetitions >= 52}
                            onClick={() => { if (repetitions < 52) setRepetitions(repetitions + 1); }}
                            sx={{
                                backgroundColor: isDarkMode ? darkTheme.cardBg : grey[100],
                                width: 32,
                                height: 32,
                                color: isDarkMode ? darkTheme.textPrimary : grey[700],
                                border: `1px solid ${isDarkMode ? darkTheme.cardBorder : grey[300]}`,
                                '&:hover': {
                                    backgroundColor: isDarkMode ? darkTheme.cardHover : grey[200],
                                    borderColor: isDarkMode ? orange[500] : blue[500],
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : grey[50],
                                    color: isDarkMode ? darkTheme.textSecondary : grey[400],
                                    borderColor: isDarkMode ? darkTheme.cardBorder : grey[200],
                                }
                            }}
                        >
                            <AddOutlined sx={{ fontSize: '1rem' }} />
                        </IconButton>
                    </Box>
                </Box>

                {/* Start KW */}
                <Box 
                    className={styles.startweek}
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: isDarkMode ? darkTheme.cardBg : 'white',
                        border: `1px solid ${isDarkMode ? darkTheme.cardBorder : grey[200]}`,
                        boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.05)',
                    }}
                >
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            color: isDarkMode ? darkTheme.textSecondary : grey[600],
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            mb: 0.5,
                            display: 'block'
                        }}
                    >
                        Start Kalenderwoche
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                            size="small"
                            onClick={() => {
                                let newWeek = startWeek - 1;
                                if (newWeek < 1) newWeek = 52;
                                onChangeStartWeek(newWeek);
                            }}
                            sx={{
                                backgroundColor: isDarkMode ? darkTheme.cardBg : grey[100],
                                width: 32,
                                height: 32,
                                color: isDarkMode ? darkTheme.textPrimary : grey[700],
                                border: `1px solid ${isDarkMode ? darkTheme.cardBorder : grey[300]}`,
                                '&:hover': {
                                    backgroundColor: isDarkMode ? darkTheme.cardHover : grey[200],
                                    borderColor: isDarkMode ? orange[500] : blue[500],
                                },
                            }}
                        >
                            <RemoveOutlined sx={{ fontSize: '1rem' }} />
                        </IconButton>
                        <CustomTextField
                            className={styles.textfield}
                            type="number"
                            variant="outlined"
                            size="small"
                            value={startWeek}
                            onChange={(e) => {
                                const val = Number(e.target.value);
                                if (val >= 1 && val <= 52) onChangeStartWeek(val);
                            }}
                            sx={{
                                width: 70,
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: isDarkMode ? darkTheme.cardBg : 'white',
                                    borderRadius: 1,
                                    '& input': {
                                        textAlign: 'center',
                                        fontWeight: 600,
                                        fontSize: '1rem',
                                        padding: '6px 8px',
                                        MozAppearance: 'textfield',
                                        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                            WebkitAppearance: 'none',
                                            margin: 0,
                                        },
                                    },
                                    '& fieldset': { borderColor: isDarkMode ? darkTheme.cardBorder : grey[300] },
                                    '&:hover fieldset': { borderColor: isDarkMode ? orange[500] : blue[500] },
                                    '&.Mui-focused fieldset': { borderColor: isDarkMode ? orange[500] : blue[600] },
                                },
                                '& .MuiInputBase-input': { color: isDarkMode ? darkTheme.textPrimary : grey[800] },
                            }}
                        />
                        <IconButton
                            size="small"
                            onClick={() => {
                                let newWeek = startWeek + 1;
                                if (newWeek > 52) newWeek = 1;
                                onChangeStartWeek(newWeek);
                            }}
                            sx={{
                                backgroundColor: isDarkMode ? darkTheme.cardBg : grey[100],
                                width: 32,
                                height: 32,
                                color: isDarkMode ? darkTheme.textPrimary : grey[700],
                                border: `1px solid ${isDarkMode ? darkTheme.cardBorder : grey[300]}`,
                                '&:hover': {
                                    backgroundColor: isDarkMode ? darkTheme.cardHover : grey[200],
                                    borderColor: isDarkMode ? orange[500] : blue[500],
                                },
                            }}
                        >
                            <AddOutlined sx={{ fontSize: '1rem' }} />
                        </IconButton>
                    </Box>
                </Box>

                {/* Jahr */}
                <Box 
                    className={styles.year}
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: isDarkMode ? darkTheme.cardBg : 'white',
                        border: `1px solid ${isDarkMode ? darkTheme.cardBorder : grey[200]}`,
                        boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.05)',
                    }}
                >
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            color: isDarkMode ? darkTheme.textSecondary : grey[600],
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            mb: 0.5,
                            display: 'block'
                        }}
                    >
                        Jahr
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                            size="small"
                            onClick={() => setYear(year - 1)}
                            sx={{
                                backgroundColor: isDarkMode ? darkTheme.cardBg : grey[100],
                                width: 32,
                                height: 32,
                                color: isDarkMode ? darkTheme.textPrimary : grey[700],
                                border: `1px solid ${isDarkMode ? darkTheme.cardBorder : grey[300]}`,
                                '&:hover': {
                                    backgroundColor: isDarkMode ? darkTheme.cardHover : grey[200],
                                    borderColor: isDarkMode ? orange[500] : blue[500],
                                },
                            }}
                        >
                            <RemoveOutlined sx={{ fontSize: '1rem' }} />
                        </IconButton>
                        <CustomTextField
                            className={styles.textfield}
                            type="number"
                            variant="outlined"
                            size="small"
                            value={year}
                            onChange={(e) => setYear(Number(e.target.value))}
                            sx={{
                                width: 85,
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: isDarkMode ? darkTheme.cardBg : 'white',
                                    borderRadius: 1,
                                    '& input': {
                                        textAlign: 'center',
                                        fontWeight: 600,
                                        fontSize: '1rem',
                                        padding: '6px 8px',
                                        MozAppearance: 'textfield',
                                        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                            WebkitAppearance: 'none',
                                            margin: 0,
                                        },
                                    },
                                    '& fieldset': { borderColor: isDarkMode ? darkTheme.cardBorder : grey[300] },
                                    '&:hover fieldset': { borderColor: isDarkMode ? orange[500] : blue[500] },
                                    '&.Mui-focused fieldset': { borderColor: isDarkMode ? orange[500] : blue[600] },
                                },
                                '& .MuiInputBase-input': { color: isDarkMode ? darkTheme.textPrimary : grey[800] },
                            }}
                        />
                        <IconButton
                            size="small"
                            onClick={() => setYear(year + 1)}
                            sx={{
                                backgroundColor: isDarkMode ? darkTheme.cardBg : grey[100],
                                width: 32,
                                height: 32,
                                color: isDarkMode ? darkTheme.textPrimary : grey[700],
                                border: `1px solid ${isDarkMode ? darkTheme.cardBorder : grey[300]}`,
                                '&:hover': {
                                    backgroundColor: isDarkMode ? darkTheme.cardHover : grey[200],
                                    borderColor: isDarkMode ? orange[500] : blue[500],
                                },
                            }}
                        >
                            <AddOutlined sx={{ fontSize: '1rem' }} />
                        </IconButton>
                    </Box>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

export default CreateWeeklyScheduleSettings;
